import { config } from "config";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BookingState,
  SetBookingsPayload,
  BookingLoadingPayload,
  UpdateBookingPayload,
  RemoveBookingPayload,
  SetFiltersPayload,
  SetPagePayload,
  BookingType,
  AdminNotePayload,
  SetTotalCountPayload,
} from ".";
import { normalizeSortOrder, parseSortString } from "utils/sort.utils";

const default_page_size = config.PAGE_SIZE;
const initialBooking = {
  bookings: [],
  loading: true,
  current_filters: {},
  filters: { page: 1, pageSize: default_page_size },
  notes: [],
};
const initialState: BookingState = {
  tab: 0,
  refresh: 0,
  booking: null,
  loading: false,
  csvMessage: null,
  exportCSVLoading: false,
  refreshLoader: false,
  confirmed: initialBooking,
  in_progress: initialBooking,
  pending: initialBooking,
  completed: initialBooking,
  bookings: initialBooking,
  cancelled: initialBooking,
  failed: initialBooking,
  awaiting: initialBooking,
  totalCount: 0,
};

export const deliveryBookingSlice = createSlice({
  name: "deliveryBooking",
  initialState,
  reducers: {
    refresh: (state) => {
      state.refresh += 1;
      state.refreshLoader = true;
    },
    setTab: (state, action: PayloadAction<number>) => {
      state.tab = action.payload;
    },
    setPage: (state, action: PayloadAction<SetPagePayload>) => {
      const { type, page } = action.payload;
      state[type].filters.page = page;
      state.refresh += 1;
      state.refreshLoader = true;
    },
    resetPage: (state, action: PayloadAction<BookingType>) => {
      const type = action.payload;
      state[type].filters.page = 1;
    },
    setFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
      const { type, data } = action.payload;
      state[type].filters = data;
      state.refreshLoader = true;
    },
    setSort: (
      state,
      action: PayloadAction<{
        type: BookingType;
        sortBy: string;
        sortOrder: 1 | -1 | "asc" | "desc";
      }>
    ) => {
      const { type, sortBy, sortOrder } = action.payload;
      state[type].filters.sortBy = sortBy;
      // Convert to numeric format for consistency using utility function
      state[type].filters.sortOrder = normalizeSortOrder(sortOrder);
      state.refresh += 1;
      state.refreshLoader = true;
    },
    setSorts: (
      state,
      action: PayloadAction<{
        type: BookingType;
        sorts: { sortBy: string; sortOrder: 1 | -1 | "asc" | "desc" }[];
      }>
    ) => {
      const { type, sorts } = action.payload;
      const normalized = sorts.map((s) => ({
        sortBy: s.sortBy,
        sortOrder: normalizeSortOrder(s.sortOrder),
      }));
      state[type].filters.sorts = normalized;
      if (normalized.length > 0) {
        state[type].filters.sortBy = normalized[0].sortBy;
        state[type].filters.sortOrder = normalized[0].sortOrder;
      } else {
        delete (state[type].filters as any).sortBy;
        delete (state[type].filters as any).sortOrder;
      }
      state.refresh += 1;
      state.refreshLoader = true;
    },
    setSortFromString: (
      state,
      action: PayloadAction<{
        type: BookingType;
        sortString: string;
      }>
    ) => {
      const { type, sortString } = action.payload;
      const sorts = parseSortString(sortString);
      state[type].filters.sorts = sorts;
      if (sorts.length > 0) {
        state[type].filters.sortBy = sorts[0].sortBy;
        state[type].filters.sortOrder = sorts[0].sortOrder;
      } else {
        delete (state[type].filters as any).sortBy;
        delete (state[type].filters as any).sortOrder;
      }
    },
    resetFilters: (state, action: PayloadAction<BookingType>) => {
      state.refresh += 1;
      state.refreshLoader = true;
      state[action.payload].filters = initialBooking.filters;
      state[action.payload].current_filters = initialBooking.current_filters;
    },
    setCurrentFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
      const { type, data } = action.payload;
      state[type].current_filters = data;
    },
    setLoader: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoading: (state, action: PayloadAction<BookingLoadingPayload>) => {
      const { type, loading } = action.payload;
      state[type].loading = loading;
    },
    setExportCSVLoading: (state, action: PayloadAction<boolean>) => {
      state.exportCSVLoading = action.payload;
    },
    setCsvMessage: (
      state,
      action: PayloadAction<{
        message: string;
        type: "success" | "error" | "warning" | "info";
      } | null>
    ) => {
      state.csvMessage = action.payload;
    },
    addBooking: (state: any, action) => {
      // state.bookings["upcoming"].unshift(action.payload);
      state?.["upcoming"].bookings.unshift(action.payload);
    },
    updateBooking: (state, action: PayloadAction<UpdateBookingPayload>) => {
      const { id, type, booking } = action.payload;
      state[type].bookings.every(({ _id }, i) => {
        if (id === _id) {
          state[type].bookings[i] = booking;
          return false;
        }
        return true;
      });
    },
    removeBooking: (state, action: PayloadAction<RemoveBookingPayload>) => {
      const { id, type } = action.payload;
      state[type].bookings.every(({ _id }, i) => {
        if (id === _id) {
          state[type].bookings.splice(i, 1);
          return false;
        }
        return true;
      });
    },
    setBooking: (state, action) => {
      state.booking = action.payload;
    },

    setTotalCount: (state, action: PayloadAction<SetTotalCountPayload>) => {
      const { totalCount } = action.payload;
      state.totalCount = totalCount;
      // state.refresh += 1;
      // state.refreshLoader = true;
    },
    updateBookingDetails: (state, action) => {
      const booking = action.payload;
      if (booking?._id === state.booking?._id) state.booking = booking;
    },
    setBookings: (state, action: PayloadAction<SetBookingsPayload>) => {
      const { type, bookings } = action.payload;
      state.refreshLoader = false;
      state[type].bookings = bookings;
    },
    acceptBooking: (state, action: PayloadAction<UpdateBookingPayload>) => {
      const { id, type, booking } = action.payload;
      state[type].bookings.every(({ _id }, i) => {
        if (id === _id) {
          state[type].bookings.splice(i, 1);
          state["in_progress"].bookings.unshift(booking);
          return false;
        }
        return true;
      });
    },
    giveBack: (state, action: PayloadAction<UpdateBookingPayload>) => {
      const { id, type, booking } = action.payload;

      state[type].bookings.every(({ _id }, i) => {
        if (id === _id) {
          state[type].bookings.splice(i, 1);
          state["confirmed"].bookings.unshift(booking);
          return false;
        }
        return true;
      });
    },
    addAdminNote: (state, action: PayloadAction<AdminNotePayload>) => {
      const { bookingId, note, type } = action.payload;
      const bookingTypeState = state[type];
      const bookingIndex = bookingTypeState.bookings.findIndex(
        ({ _id }) => _id === bookingId
      );

      if (bookingIndex !== -1) {
        if (!bookingTypeState.bookings[bookingIndex].notes) {
          bookingTypeState.bookings[bookingIndex].notes = [];
        }
        bookingTypeState.bookings[bookingIndex].notes.push({
          note: note,
          createdAt: new Date().toISOString(),
        });
      }
    },
  },
});

const deliveryBookingReducer = deliveryBookingSlice.reducer;

export const deliveryBookingActions = deliveryBookingSlice.actions;
export default deliveryBookingReducer;
